import { useMemo, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import type { ScenarioDto } from '@/api-gen/api.schemas';
import logger from 'app/utils/logger.ts';

/**
 * Recently-opened scenarios.
 *
 * There is no backend "recent scenarios" endpoint yet, so we track the ids the
 * user opens on this device in localStorage. This module is the single seam to
 * swap for a real endpoint later: replace {@link getSnapshot}/{@link recordRecentScenario}
 * with the API call and keep the same hook surface.
 */
const STORAGE_KEY = 'sklls.recentScenarioIds';
const CAP = 12;

const readFromStorage = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === 'number').slice(0, CAP);
  } catch (error) {
    logger.warn('Failed to read recent scenarios from storage', error);
    return [];
  }
};

let ids: number[] = readFromStorage();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

// Cross-tab sync. Attached only while there is at least one subscriber and torn
// down on the last unsubscribe, so the listener doesn't leak — a module-level
// registration would stack duplicates under HMR / repeated test imports.
const handleStorage = (event: StorageEvent) => {
  if (event.key === STORAGE_KEY) {
    ids = readFromStorage();
    emit();
  }
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    // Catch up on any change made while nothing was subscribed, then listen for more.
    ids = readFromStorage();
    window.addEventListener('storage', handleStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

const getSnapshot = () => ids;

/** Move a scenario to the front of the recent list (deduped, capped). */
export const recordRecentScenario = (scenarioId?: number | null) => {
  if (!scenarioId) return;
  ids = [scenarioId, ...ids.filter((id) => id !== scenarioId)].slice(0, CAP);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    logger.warn('Failed to persist recent scenarios', error);
  }
  emit();
};

/** Reactive list of recent scenario ids (most-recent-first). */
export const useRecentScenarioIds = (): number[] => useSyncExternalStore(subscribe, getSnapshot);

/**
 * Recent scenarios resolved against the Redux scenario store, most-recent-first,
 * limited to `limit`. Ids that no longer resolve to a scenario are dropped.
 */
export const useRecentScenarios = (limit = 5): ScenarioDto[] => {
  const recentIds = useRecentScenarioIds();
  const scenarios = useSelector(ScenarioSelectors.scenariosOrvalSelector);

  return useMemo(() => {
    const byId = new Map(scenarios.map((scenario) => [scenario.id, scenario]));
    return recentIds
      .map((id) => byId.get(id))
      .filter((scenario): scenario is ScenarioDto => !!scenario)
      .slice(0, limit);
  }, [recentIds, scenarios, limit]);
};
